import { QuestionFilter } from '@/components/elements/questions/questions-heading.component';
import { Answer, Product, Question, TailoringProcess, Template, YesNoQuestion } from '@/gql/graphql';
import { getProduct } from '@/pages/products/products.requests';
import { getTemplate } from '@/pages/templates/templates.requests';
import { useApolloClient } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useAnswerQuestion } from '../use-answer-question.hook';
import {
  calculateProgress,
  filterQuestionsByFilter,
  filterQuestionsBySearch,
  initialFilterSort,
  isEveryQuestionAnswered,
} from './use-questions.hook.core';

type QuestionsHook = {
  progress: number;
  allAnswered: boolean;
  search: string;
  filter: QuestionFilter;
  questionsLoading: boolean;
  answerInProgress: boolean;
  answerMutationLoading: boolean;
  templateAnswerMutationLoading: boolean;
  radioQuestions: YesNoQuestion[];
  handleQuestionFilter: (search: string, filter: QuestionFilter) => void;
  updateQuestion: (radioQuestion: YesNoQuestion, answer: Answer, product?: Product) => void;
  updateTemplateQuestion: (radioQuestion: YesNoQuestion, answer: Answer, template?: Template) => void;
};

type QuestionsHookProps = {
  tailoringProcess?: TailoringProcess;
  id?: string;
};

export const useQuestions = ({ tailoringProcess, id }: QuestionsHookProps): QuestionsHook => {
  const client = useApolloClient();

  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<QuestionFilter>('ALL');
  const [radioQuestions, setRadioQuestions] = useState<YesNoQuestion[] | undefined>(undefined);

  const [answerInProgress, setAnswerInProgress] = useState(false);

  const { answerSingleQuestion, answerLoading, answerSingleTemplateQuestion, templateAnswerLoading } =
    useAnswerQuestion(id);

  // used for setting the local state of the questions
  useEffect(() => {
    if (!tailoringProcess) return;
    if (radioQuestions) return;
    setRadioQuestions(initialFilterSort(tailoringProcess.questions));
    setLoading(false);
  }, [tailoringProcess]);

  // called when the user answers a question, updating the apollo cache and with that the local state
  const updateQuestion = async (radioQuestion: YesNoQuestion, answer: Answer, product?: Product) => {
    if (!product) return;
    setAnswerInProgress(true);

    // mutation call
    await answerSingleQuestion(radioQuestion.id, answer);

    const cachedQuestionId = client.cache.identify(radioQuestion);

    client.cache.modify({
      id: cachedQuestionId,
      fields: {
        answer() {
          return answer;
        },
      },
    });

    const cachedProduct = client.readQuery({
      query: getProduct,
      variables: {
        id: product.id,
      },
    }) as {
      product: Product;
    };

    setAnswerInProgress(false);
    // we're using the cached product to update the questions, since the filters need to be applied
    handleQuestionFilter(search, filter, cachedProduct.product.tailoringProcess.questions);
  };

  const updateTemplateQuestion = async (radioQuestion: YesNoQuestion, answer: Answer, template?: Template) => {
    if (!template) return;
    setAnswerInProgress(true);

    await answerSingleTemplateQuestion(radioQuestion.id, answer);

    const cachedQuestionId = client.cache.identify(radioQuestion);

    client.cache.modify({
      id: cachedQuestionId,
      fields: {
        answer() {
          return answer;
        },
      },
    });

    const cachedTemplate = client.readQuery({
      query: getTemplate,
      variables: {
        id: template.id,
      },
    }) as {
      template: Template;
    };

    setAnswerInProgress(false);
    handleQuestionFilter(search, filter, cachedTemplate.template.tailoringProcess.questions);
  };

  // called when the filters/search change and when the user answers a question
  const handleQuestionFilter = (search: string, filter: QuestionFilter, questions?: Question[]) => {
    if (!tailoringProcess) return;
    if (!questions) questions = tailoringProcess.questions;

    let filtered = filterQuestionsByFilter(initialFilterSort(questions), filter);
    if (!filtered) return;
    filtered = filterQuestionsBySearch(filtered, search);
    setSearch(search);
    setFilter(filter);
    setRadioQuestions(filtered);
  };

  return {
    search,
    filter,
    updateQuestion,
    updateTemplateQuestion,
    handleQuestionFilter,
    answerMutationLoading: answerLoading,
    templateAnswerMutationLoading: templateAnswerLoading,
    questionsLoading: loading,
    answerInProgress,
    radioQuestions: radioQuestions || [],
    progress: calculateProgress(tailoringProcess?.questions),
    allAnswered: isEveryQuestionAnswered(initialFilterSort(tailoringProcess?.questions) || []),
  };
};
